(() => {
  if (customElements.get('bba-shopping-bar')) return;
  class ShoppingBar extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-shopping-config]').textContent);
      this.action = this.querySelector('.bba-shopping-bar__action');
      this.label = this.querySelector('[data-shopping-action]');
      this.titleNode = this.querySelector('.bba-shopping-bar__title');
      this.price = this.querySelector('[data-shopping-price]');
      this.variantLabel = this.querySelector('[data-shopping-variant]');
      this.confirmed = !this.config.hasVariants || Boolean(this.config.selected);
      this.cart = this.config.cart;
      this.onAction = event => this.activate(event);
      this.action.addEventListener('click', this.onAction);
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
      };
      this.onChange = event => {
        if (this.info?.contains(event.target) && event.target.closest('variant-selects, variant-radios, [data-variant-picker]')) this.confirmed = true;
        this.schedule();
      };
      document.addEventListener('change', this.onChange);
      window.addEventListener('scroll', this.schedule, { passive: true });
      window.addEventListener('resize', this.schedule, { passive: true });
      this.onFocus = () => { if (this.dataset.mode === 'cart') this.refreshCart(); else this.schedule(); };
      window.addEventListener('pageshow', this.onFocus);
      window.addEventListener('focus', this.onFocus);
      this.observer = new MutationObserver(changes => {
        if (changes.every(change => this.contains(change.target) || change.target === document.documentElement)) return;
        this.schedule();
      });
      this.observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class','open','disabled','aria-disabled','value'] });
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.reserveSpace());
        this.resizeObserver.observe(this);
      }
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCart());
      }
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && this.dataset.mode === 'cart') {
        this.cartObserver = new MutationObserver(() => this.refreshCart());
        this.cartObserver.observe(bubble, { childList:true, subtree:true, characterData:true });
      }
      if (this.dataset.mode === 'cart') this.refreshCart();
      this.update();
    }
    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style:'currency', currency:this.cart.currency || this.config.currency || 'PKR' }).format(Number(cents) / 100);
    }
    text(node, value) { if (node.textContent !== value) node.textContent = value; }
    overlayOpen() {
      return Boolean(document.querySelector('cart-drawer.active, cart-notification.active, dialog[open], quick-add-modal[open], .menu-drawer-container[open]'));
    }
    visible(element) {
      if (!element || !element.getClientRects().length) return false;
      const rect = element.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
    }
    productState() {
      this.info = document.querySelector('product-info[id^="MainProduct-"]') || document.querySelector('main product-info');
      const form = this.info?.querySelector('product-form form[data-type="add-to-cart-form"]');
      this.original = form?.querySelector('.product-form__submit');
      this.picker = this.info?.querySelector('variant-selects, variant-radios, [data-variant-picker]');
      const id = form?.querySelector('[name=id]')?.value;
      const variant = (this.config.variants || []).find(item => String(item.id) === String(id));
      const busy = Boolean(this.original?.classList.contains('loading') || this.original?.getAttribute('aria-disabled') === 'true');
      return { variant, busy, disabled:!this.original || this.original.disabled || busy };
    }
    update() {
      let show = false;
      if (this.dataset.mode === 'cart') {
        show = this.cart.item_count > 0;
        this.text(this.titleNode, this.config.strings.items.replace('__COUNT__', this.cart.item_count));
        this.text(this.variantLabel, this.config.strings.subtotal);
        this.text(this.price, this.money(this.cart.total_price));
        this.text(this.label, this.config.strings.view);
        this.action.removeAttribute('aria-disabled');
      } else {
        const state = this.productState();
        const choose = this.config.hasVariants && (!this.confirmed || !state.variant);
        this.mode = this.config.bundle ? 'bundle' : choose ? 'choose' : 'add';
        let label = this.config.strings.add;
        if (this.mode === 'bundle') label = this.config.strings.build;
        else if (choose) label = this.config.strings.choose;
        else if (state.busy) label = this.config.strings.adding;
        else if (state.disabled) label = state.variant && !state.variant.available ? this.config.strings.soldOut : this.config.strings.unavailable;
        this.text(this.label, label);
        this.text(this.variantLabel, this.config.hasVariants && this.confirmed && state.variant ? state.variant.title : '');
        this.text(this.price, state.variant ? this.money(state.variant.price) : '');
        this.action.setAttribute('aria-disabled', String(this.mode === 'add' && state.disabled));
        show = this.config.bundle ? Boolean(this.info || document.querySelector('main')) : Boolean(this.original || this.picker);
        if (this.config.bundle) show = show && window.scrollY > 300;
        else show = show && Boolean(this.original && this.original.getBoundingClientRect().bottom <= 0);
      }
      show = show && !this.overlayOpen();
      this.hidden = !show;
      this.reserveSpace();
    }
    reserveSpace() {
      const height = this.hidden ? 0 : Math.ceil(this.getBoundingClientRect().height);
      const value = `${height}px`;
      if (document.documentElement.style.getPropertyValue('--bba-shopping-height') !== value) document.documentElement.style.setProperty('--bba-shopping-height',value);
    }
    reveal(target) {
      if (!target) return;
      target.scrollIntoView({ behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block:'center' });
      const focus = target.matches('input,select,button,a') ? target : target.querySelector('input:not([type=hidden]):not(:disabled),select,button:not(:disabled),a');
      if (focus) focus.focus({preventScroll:true});
      else { target.setAttribute('tabindex','-1'); target.focus({preventScroll:true}); }
    }
    activate(event) {
      if (this.dataset.mode === 'cart') {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          event.preventDefault();
          drawer.open(document.getElementById('cart-icon-bubble') || this.action);
          this.schedule();
        }
        return;
      }
      event.preventDefault();
      this.update();
      if (this.mode === 'bundle') {
        const target = document.querySelector('[id*="bundle-builder"], [class*="bundle-builder-app"], [id*="kitenzo"], [class*="kitenzo"]') || this.info || document.querySelector('main');
        this.reveal(target);
      } else if (this.mode === 'choose') this.reveal(this.picker || this.original || this.info);
      else if (this.action.getAttribute('aria-disabled') !== 'true') {
        this.original.click();
        this.schedule();
      }
    }
    async refreshCart() {
      if (this.dataset.mode !== 'cart') return;
      clearTimeout(this.cartTimer);
      this.cartTimer = setTimeout(async () => {
        this.abort?.abort(); this.abort = new AbortController();
        try {
          const response = await fetch(`${this.dataset.rootUrl}cart.js`, { credentials:'same-origin', cache:'no-store', signal:this.abort.signal });
          if (!response.ok) return;
          this.cart = await response.json(); this.update();
        } catch (_) {}
      }, 80);
    }
    disconnectedCallback() {
      this.observer?.disconnect(); this.resizeObserver?.disconnect(); this.cartObserver?.disconnect(); this.unsubscribe?.(); this.abort?.abort();
      clearTimeout(this.cartTimer); cancelAnimationFrame(this.frame);
      this.action.removeEventListener('click',this.onAction);
      document.removeEventListener('change',this.onChange);
      window.removeEventListener('scroll',this.schedule); window.removeEventListener('resize',this.schedule);
      window.removeEventListener('pageshow',this.onFocus); window.removeEventListener('focus',this.onFocus);
      document.documentElement.style.removeProperty('--bba-shopping-height');
    }
  }
  customElements.define('bba-shopping-bar',ShoppingBar);
})();
